// needs-decision-outcome-recording.ts — V2-1 (spec V2 §4.1 "Khi auto", E4, E6).
//
// Ghi kết quả của một `needs_decision` MỚI (không phải câu lặp):
//  - auto ⇒ câu hỏi vào interview.json, journal `decision_question_asked{mode:auto}`
//    + `decision_auto_applied`, RỒI câu trả lời (option id) nguồn
//    `delegated:<G0 id>` (review M1: journal trước, answer ghi bù được);
//  - ask ⇒ câu required như V2-0; policy hiệu lực `auto` mà không áp ⇒ thêm
//    dòng "không auto vì …" (E4) + `decision_auto_skipped`. Policy `ask` ⇒
//    không E4, không skipped — chỉ `decision_question_asked{mode:ask}` (E6).
import { randomUUID } from "node:crypto";
import { recordDelegatedAnswer } from "./decision-auto-delegated-answer-reconcile.js";
import {
  decisionAutoAppliedDelta,
  decisionAutoSkippedDelta,
  decisionQuestionAskedDelta,
  redactDecisionText,
} from "./decision-policy-journal-events.js";
import { writeInterviewQuestions } from "./interview-store.js";
import type { MissionCommitInput, MissionStore } from "./mission-store.js";
import type { NeedsDecisionAutoOutcome } from "./needs-decision-auto-decision-evaluation.js";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

export interface RecordNeedsDecisionOutcomeInput {
  controlRoot: string;
  store: MissionStore;
  question: InterviewQuestion;
  /** Câu giữ lại (ticket khác + câu đã trả lời của ticket này). */
  others: InterviewQuestion[];
  auto: NeedsDecisionAutoOutcome;
  recommendedOptionId: string | undefined;
  now: Date;
  /** Codex F9: known-values hiện tại — che chữ thô của worker trước khi cắt 4000. */
  knownValues: readonly string[];
}

/** E4: một dòng trong text câu hỏi — chỉ khi policy hiệu lực = `auto`. */
function withNoAutoLine(question: InterviewQuestion, reason: string): InterviewQuestion {
  // Lý do do controller tự sinh (tên token/lỗi), không phải chữ worker ⇒ không cần known-values.
  const line = `không auto vì ${redactDecisionText(reason, [])}`;
  return {
    ...question,
    consequence: question.consequence === undefined ? line : `${question.consequence}\n${line}`,
  };
}

async function commit(store: MissionStore, deltas: MissionCommitInput["delta"][]): Promise<void> {
  await store.commit(deltas.map((delta) => ({ event_id: randomUUID(), delta })));
}

/** `true` khi quyết định đã được tự áp (câu có answer `delegated`). */
export async function recordNeedsDecisionOutcome(
  input: RecordNeedsDecisionOutcomeInput,
): Promise<boolean> {
  const { question, auto } = input;
  const write = (q: InterviewQuestion) =>
    writeInterviewQuestions({
      controlRoot: input.controlRoot,
      questions: [...input.others, q],
      now: () => input.now,
    });

  if (auto.mode === "auto") {
    // Review M1: journal (nguồn sự thật của sổ + trần) TRƯỚC answer. Chết giữa
    // hai bước ⇒ `startSupervisor` ghi bù answer từ journal
    // (decision-auto-delegated-answer-reconcile.ts); commit lỗi ⇒ ném lên,
    // câu đã ghi vẫn chưa trả lời ⇒ chờ người — không bao giờ áp ngầm.
    await write(question);
    await commit(input.store, [
      decisionQuestionAskedDelta(question, "auto", input.recommendedOptionId, input.knownValues),
      decisionAutoAppliedDelta({
        question,
        optionId: auto.option.id,
        optionLabel: auto.option.label,
        claims: auto.claims,
        g0RequestId: auto.g0RequestId,
        blueprintHash: auto.blueprintHash,
        decidedAt: input.now.toISOString(),
        secretValues: input.knownValues,
      }),
    ]);
    await recordDelegatedAnswer(
      input.controlRoot,
      question.id,
      auto.option.id,
      auto.g0RequestId,
      input.now,
    );
    return true;
  }

  const skipReason = auto.skipReason;
  await write(skipReason === undefined ? question : withNoAutoLine(question, skipReason));
  await commit(input.store, [
    decisionQuestionAskedDelta(question, "ask", input.recommendedOptionId, input.knownValues),
    ...(skipReason === undefined ? [] : [decisionAutoSkippedDelta(question.id, skipReason)]),
  ]);
  return false;
}
